import {
  NOTIFICATION_TERMS_MOCK_STATUSES,
  type SiteNotificationConditionMock,
  type NotificationTermsMockStatus,
} from '~/types/siteNotification'

type QueryValue = string | string[] | null | undefined

const CONDITION_MOCK_FLAG = 'siteNotificationConditionMock'
const CONDITION_MOCK_LOCALE = 'siteNotificationLocale'
const CONDITION_MOCK_COUNTRY = 'siteNotificationCountry'
const CONDITION_MOCK_STORE_ID = 'siteNotificationStoreId'
const CONDITION_MOCK_ROLE = 'siteNotificationRole'
const CONDITION_MOCK_TERMS = 'siteNotificationTerms'

const unique = (values: string[]): string[] => Array.from(new Set(values))

const queryValues = (query: Record<string, unknown>, key: string): string[] => {
  const value = query[key] as QueryValue
  const values = Array.isArray(value) ? value : [value]

  return unique(
    values
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean),
  )
}

const queryTermsStatus = (
  query: Record<string, unknown>,
): NotificationTermsMockStatus | undefined => {
  const value = queryValues(query, CONDITION_MOCK_TERMS)[0]
  return (NOTIFICATION_TERMS_MOCK_STATUSES as readonly string[]).includes(value)
    ? (value as NotificationTermsMockStatus)
    : undefined
}

/** Parse the development-only query context without changing the route. */
export const getSiteNotificationConditionMock = (
  query: Record<string, unknown>,
): SiteNotificationConditionMock | null => {
  const enabled = queryValues(query, CONDITION_MOCK_FLAG).includes('1')
  if (!enabled) return null

  const termsStatus = queryTermsStatus(query)

  return {
    locale: queryValues(query, CONDITION_MOCK_LOCALE)[0] || 'en',
    country: queryValues(query, CONDITION_MOCK_COUNTRY)[0] || 'US',
    storeIds: queryValues(query, CONDITION_MOCK_STORE_ID),
    roles: queryValues(query, CONDITION_MOCK_ROLE),
    ...(termsStatus ? { termsStatus } : {}),
  }
}

/** Keep each query condition isolated from claims/dismissals in sessionStorage. */
export const getSiteNotificationConditionMockAccountId = (
  condition: SiteNotificationConditionMock,
): string => {
  const key = [
    condition.locale.trim().toLowerCase(),
    condition.country.trim().toLowerCase(),
    [...condition.storeIds].sort().join(','),
    [...condition.roles].sort().join(','),
    condition.termsStatus || '',
  ]
    .map((value) => encodeURIComponent(value))
    .join(':')

  return `dev-site-notification-condition:${key}`
}
